import { AutoComplete, type Option } from "@/components/ui/autocomplete";
import { RootState } from "@/redux/store";
import CustomCollapsible from "@/components/ui/collapsibleCard/collapsible-card";
import { Input } from "@/components/ui/input";
import { useEffect, useMemo } from "react";
import {
  Control,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { useSelector } from "react-redux";
import { Check, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { toast } from "@/components/ui/use-toast";

import { useGetIncomeCategorListQuery } from "@/services/incomeCategoryApi";
import { useGetSalesTaxListQuery } from "@/services/salesTaxApi";
import { useGetMembershipsQuery } from "@/services/membershipsApi";
import { displayDate, formatToPKR } from "@/lib/helper";
import { useGetOrgTaxTypeQuery } from "@/services/organizationApi";
import { BaseItem, InvoicePayload } from "@/types/invoice";
import { ProductType } from "@/types/enums";
import { format } from "date-fns";
import Combobox from "@/components/ui/common/combobox";

interface InvoiceHeaderProps {
  control: Control<InvoicePayload>;
  errors: FieldErrors<InvoicePayload>;
  watch: UseFormWatch<InvoicePayload>;
  setValue: UseFormSetValue<InvoicePayload>;
  action: string
}

interface InvoiceItem {
  id: number;
  item_id: number;
  item_type: ProductType;
  description: string;
  quantity: number;
  price: number;
  income_category_id: number; //id
  sale_tax_id: number; //id
  tax_rate: number;
  discount: number;
  sub_total: number;
  net_price: number;
  tax_type: string;
  tax_name: string;
  tax_perc: number;
  revenue_type: string;
  is_srb: boolean;
  total: number;
  tax_amount: number;
  booking_date: string;
}

const initialInvoiceItem: InvoiceItem = {
  id: 1,
  item_id: 0,
  item_type: ProductType.Membership,
  description: "",
  income_category_id: 0,
  quantity: 0,
  sale_tax_id: 0,
  price: 0,
  tax_rate: 0,
  discount: 0,
  sub_total: 0,
  net_price: 0,
  tax_type: '',
  tax_name: "",
  total: 0,
  tax_perc: 0,
  revenue_type: "income",
  is_srb: false,
  tax_amount: 0,
  booking_date: format(new Date(), "yyyy-MM-dd"),
};

const InvoiceItemsDetails = ({
  control,
  errors,
  watch,
  setValue,
  action
}: InvoiceHeaderProps) => {
  const watcher = watch()
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const userid =
    useSelector((state: RootState) => state.auth.userInfo?.user.id) || 0;
  const { data: orgTaxType } = useGetOrgTaxTypeQuery(orgId) || {
    tax_type: "exclusive",
  };
  const { data: incomeCatData } = useGetIncomeCategorListQuery(orgId);
  const { data: salesTaxData } = useGetSalesTaxListQuery(orgId);
  const { data: membershipsData } = useGetMembershipsQuery({
    org_id: orgId,
    query: "status=active",
  });

  const incomeCatOptions = useMemo(() => {
    return Array.isArray(incomeCatData) ? incomeCatData?.map((cat) => ({
      value: cat.id.toString(),
      label: cat.name
    })) : []
  }, [incomeCatData])

  const membershipOptions = useMemo(() => {
    return Array.isArray(membershipsData?.data) ? membershipsData?.data.map((membership) => ({
      ...membership,
      value: membership.id.toString(),
      label: membership.name
    })) : []
  }, [membershipsData])

  const [items, setItems] = useState<InvoiceItem[]>([initialInvoiceItem]);

  const addItem = () => {
    const newItem: InvoiceItem = {
      id: items.length + 1,
      net_price: 0,
      item_id: items.length + 1,
      item_type: ProductType.Membership,
      income_category_id: 0,
      description: "",
      quantity: 0,
      price: 0,
      tax_rate: 0,
      discount: 0,
      sub_total: 0,
      tax_type: orgTaxType?.tax_type as string,
      tax_name: "",
      sale_tax_id: 0,
      total: 0,
      tax_perc: 0,
      revenue_type: 'income',
      is_srb: false,
      tax_amount: 0,
      booking_date: format(new Date(), "yyyy-MM-dd"),
    };
    setItems((prev) => [...prev, newItem]);
  };

  const deleteItem = (id: number, type: ProductType) => {
    setItems(
      items.length === 1
        ? [initialInvoiceItem]
        : items.filter((item) => !(item.item_id === id && item.item_type === type))
    );
  };

  const updateItem = (
    id: number,
    type: ProductType,
    field: keyof InvoiceItem,
    value: string | number
  ) => {
    const updatedItems = items.map((item) => {
      if (item.item_id == id && item.item_type == type) {
        const updatedItem = { ...item, [field]: value };

        // Extract fields with defaults for calculation
        const quantity = updatedItem.quantity || 0;
        const price = updatedItem.price || 0;
        let discount = updatedItem.discount || 0;

        // Calculate subtotal first
        const subTotal = quantity * price;

        // If updating discount, validate it against subtotal
        if (field === "discount" && Number(value) >= subTotal) {
          toast({
            variant: "destructive",
            title: "Discount should be less than net amount",
          });
          return item; // Return original item without updates
        }

        // Adjust discount based on quantity changes
        if (field === "quantity") {
          const originalQuantity = item.quantity || 1;
          const discountPerUnit = discount / originalQuantity;
          discount = discountPerUnit * quantity;
        }

        // Get tax_perc: prioritize item's existing value, fallback to membership data
        let tax_perc = updatedItem.tax_perc;
        if (tax_perc == undefined || tax_perc == null) {
          // If tax_perc is missing (edit mode scenario), fetch it from membership data
          const membershipData = membershipOptions?.find(
            (membership) => membership.id === updatedItem.item_id
          );
          tax_perc = membershipData?.tax_perc || 0;
        }

        const income_category = incomeCatData?.find(
          (income_cat) => income_cat.id == updatedItem.income_category_id
        );

        const saleTaxId = income_category?.sale_tax_id as number;
        const taxData = salesTaxData?.find((saleTax) => saleTax.id == saleTaxId);
        const tax_rate = Number(tax_perc);

        // Calculate taxable amount (after discount)
        const taxableAmount = Math.max(Math.round(subTotal - discount), 0);

        let taxAmount = 0;
        let total = 0;

        // Apply tax based on the tax type
        let netPrice = 0;
        if (orgTaxType?.tax_type === "exclusive") {
          // For exclusive tax: Calculate tax on amount after discount
          taxAmount = (taxableAmount * tax_rate) / 100;
          total = taxableAmount + taxAmount;
          netPrice = taxableAmount; // Net price is the amount after discount (before tax added)
        } else if (orgTaxType?.tax_type === "inclusive") {
          // For inclusive tax: Extract tax from amount after discount
          taxAmount = taxableAmount - (taxableAmount / (1 + tax_rate / 100));
          total = taxableAmount; // For inclusive tax, total is the discounted amount
          netPrice = taxableAmount - taxAmount; // Net price is without tax
        }

        // Update fields in the item
        const returnItem = {
          ...updatedItem,
          sub_total: Math.round(subTotal),
          tax_rate: tax_rate,
          tax_perc: tax_perc, // Ensure tax_perc is stored for future updates
          tax_amount: Math.round(taxAmount),
          total: Math.round(total),
          discount: discount,
          net_price: Math.round(netPrice),
          sale_tax_id: taxData?.id as number,
          tax_name: taxData?.name as string,
          tax_type: orgTaxType?.tax_type as string
        }
        console.log({ returnItem }, "returnItem")
        return returnItem;

      }
      return item;
    });



    setItems(updatedItems);
  };



  const handlePlanSelect = (selectedPlan: any, index: number) => {

    if (selectedPlan) {
      const { name, net_price, discount, income_category_id, tax_name, id, is_srb, tax_perc, revenue_type } = selectedPlan;

      const income_category = incomeCatData?.find(
        (income_cat) => income_cat.id == income_category_id
      );

      const saleTaxId = income_category?.sale_tax_id as number;
      const taxData = salesTaxData?.find((saleTax) => saleTax.id == saleTaxId);
      const updatedItems = [...items];

      const tax_rate = Number(tax_perc);
      // Calculate subtotal (quantity * price)
      const subTotal = 1 * net_price;

      // Calculate discount as a percentage of the subtotal
      const discountAmount = (subTotal * discount) / 100;

      // Calculate taxable amount (subtotal - discount)
      const taxableAmount = Math.max(subTotal - discountAmount, 0);

      let taxAmount = 0;
      let total = 0;
      let netPriceAfterDiscount = 0;

      // Apply tax based on the taxable amount
      if (orgTaxType?.tax_type === "exclusive") {
        // For exclusive tax: Calculate tax on amount after discount
        taxAmount = Math.round((taxableAmount * tax_perc) / 100);
        total = Math.round(taxableAmount + taxAmount);
        netPriceAfterDiscount = taxableAmount; // Net price is the amount after discount (before tax added)
      } else if (orgTaxType?.tax_type === "inclusive") {
        // For inclusive tax: Extract tax from amount after discount
        taxAmount = Math.round(taxableAmount - (taxableAmount / (1 + tax_perc / 100)));
        total = taxableAmount;
        netPriceAfterDiscount = taxableAmount - taxAmount; // Net price is without tax
      }

      updatedItems[index] = {
        ...updatedItems[index],
        price: net_price,
        quantity: 1,
        discount: discountAmount,
        sale_tax_id: saleTaxId,
        income_category_id: income_category_id,
        sub_total: subTotal,
        net_price: Math.round(netPriceAfterDiscount),
        tax_type: orgTaxType?.tax_type as string,
        tax_rate: tax_rate,
        tax_perc: tax_rate,
        revenue_type: revenue_type,
        is_srb: is_srb,
        tax_name: tax_name,
        tax_amount: taxAmount,
        total: total,
        item_id: id,
        item_type: ProductType.Membership,

        description: name,
      };
      console.log({ updatedItems })
      setItems([...updatedItems]);
    }
  };

  useEffect(() => {
    if (items && items.length > 0) {
      // setValue("membership_plans", items)
      setValue("items", items)

      const subtotal = items.reduce(
        (acc, prod) => acc + prod.price * prod.quantity,
        0
      );

      const totalTax = items.reduce(
        (acc, prod) => acc + prod.tax_amount,
        0
      );

      const discountAmt = items.reduce(
        (acc, prod) => acc + prod.discount,
        0
      );

      const total = subtotal + totalTax;

      setValue("sub_total", subtotal);
      setValue("tax_amount", totalTax);
      setValue("discount", discountAmt);
      setValue("total", total - discountAmt);
    }
  }, [items])

  useEffect(() => {
    if (action == 'edit' || action == 'member-created') {
      // setItems(watcher.membership_plans as InvoiceItem[])
      setItems(watcher.items as InvoiceItem[])
    } else {
      setItems([initialInvoiceItem])
    }
  }, [action])

  return (
    <div className="h-fit rounded-2xl bg-white">
      <CustomCollapsible
        hideToggle={false}
        isOpenDefault={true}
        title="Products & Services"
      >
        <table
          cellPadding="10"
          cellSpacing={2}
          className="w-full border-spacing-2"
        >
          <thead>
            <tr className="text-left bg-gray-50 text-nowrap  capitalize ">
              <th className="p-3 font-semibold">Item</th>
              <th className="p-3 font-semibold w-40">Book date</th>
              <th className="p-3 font-semibold ">Quanity</th>
              <th className="p-3 font-semibold">Unit Price</th>
              <th className="p-3 font-semibold">Net Amount</th>
              <th className="p-3 font-semibold">Discount</th>
              <th className="p-3 font-semibold">Income Category</th>
              <th className="p-3 font-semibold">Tax Rate</th>
              <th className="p-3 font-semibold">Tax Amount</th>
              <th className="p-3 font-semibold">Total</th>
              <th className="p-3 font-semibold">Action</th>
            </tr>
          </thead>

          <tbody className="text-nowrap ">
            {items.map((item, index) => (
              <tr key={index}>
                <td className="">
                  <AutoComplete
                    options={membershipOptions ?? [{ value: "", label: "" }]}
                    emptyMessage="No results."
                    placeholder="Find something"
                    value={item.item_id && item.description ? { value: String(item.item_id), label: item.description } : undefined}
                    onValueChange={(selectedPlan) => {
                      if (items.some(item => item.item_id == selectedPlan.id)) {
                        toast({
                          variant: "destructive",
                          title: "Item is already selected",
                        })
                        return;
                      }
                      handlePlanSelect(selectedPlan, index)
                    }
                    } // Pass the index here
                  />
                </td>
                <td className="!w-fit">
                  <Input
                    type="date"
                    className={"px-2  space-x-0 invoice-booking-date max-w-[130px]"}
                    value={item.booking_date}
                    onChange={(e) => {
                      const selectedDate = new Date(e.target.value).getTime();
                      const currentDate = new Date().setHours(0, 0, 0, 0); // Start of today

                      if (selectedDate < currentDate) {
                        toast({
                          variant: "destructive",
                          title: "Validation Error",
                          description: "Date cannot be earlier than today",
                        });
                      } else {
                        item.item_id && updateItem(item.item_id, item.item_type, "booking_date", e.target.value);
                      }

                    }}
                    required
                  />
                </td>
                <td>
                  <Input
                    className="w-16 px-2"
                    type="number"
                    value={item.quantity} // Bind the quantity correctly
                    onChange={(e) => {

                      item.item_id && updateItem(item.item_id, item.item_type, "quantity", Number(e.target.value))
                    }}
                    required
                  />
                </td>
                {/* unit price */}
                <td>{formatToPKR(item.price)}</td>
                <td>{formatToPKR(item.sub_total)}</td>
                <td>
                  <Input
                    className="w-20 px-2"
                    type="number"
                    value={item.discount}
                    onChange={(e) =>
                      item.item_id && updateItem(item.item_id, item.item_type, "discount", Number(e.target.value))
                    }
                    required
                  />
                </td>
                <td className="">
                  <Combobox
                    defaultValue={item.income_category_id ? incomeCatOptions.find(cat => cat.value == String(item.income_category_id))?.label : ""} // Use description since we match by name
                    setFilter={(value: string) => {
                      const income_category_id = incomeCatOptions.find(cat => cat.label == value)?.value;
                      item.item_id && updateItem(item.item_id, item.item_type, "income_category_id", Number(income_category_id))
                    }}
                    list={incomeCatOptions}
                    label={"Income Category"}
                    showLabel={false}
                    className="!max-w-[130px]"
                  />
                </td>

                <td>
                  <span>{item.tax_rate}%</span>
                </td>
                <td>{formatToPKR(item.tax_amount)}</td>
                <td>{formatToPKR(item.total)}</td>
                <td>
                  <Button
                    onClick={() => deleteItem(item.item_id, item.item_type)}
                    variant="destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <Button onClick={addItem} variant="outline" className="mt-4">
          <Plus className="mr-2 h-4 w-4" /> Add Item
        </Button>
      </CustomCollapsible>
    </div>
  );
};

export default InvoiceItemsDetails;
